import React, { memo, useCallback, useState } from 'react';
import { View, Text, StyleSheet, SafeAreaView, StatusBar, TouchableOpacity, FlatList, Platform } from 'react-native';
import * as FileSystem from "expo-file-system";
import BackIcon from '../assets/images/back.svg';
import DownloadIcon from '../assets/images/download.svg';
import { COLORS, ROUTES } from '../constants';
import { Icon } from '../lib/Icon';
import { NavigationProps } from './Home';
import { HymnProps, ItemProps } from '../types';
import { useGlobalContext } from '../context/GlobalContext';
import RemoveIcon from '../assets/images/remove.svg';
import { openDatabase } from '../database/database';
import { getNameAudio, getUriAudio } from '../components/Player';

const Item = memo(({item, onPress, selected, authors, onDelete}: ItemProps) => {
  return (
    <View style={[stylesItem.container]}>
      <TouchableOpacity onPress={onPress} style={[stylesItem.item]}>
        <View style={stylesItem.avatar}>
          <Text style={stylesItem.number}>{item.number}</Text>
        </View>
        <View style={{ flex: 1 }}>
          <Text style={[
            stylesItem.title,
            { color: selected ? COLORS.warning : COLORS.text }
          ]}>{item.title}</Text>
          <Text style={stylesItem.author}>{item.author?.text}</Text>
        </View>
      </TouchableOpacity>
      <TouchableOpacity onPress={onDelete} style={stylesItem.buttonRight}>
        <Icon><RemoveIcon /></Icon>
      </TouchableOpacity>
    </View>
  )
})

const stylesItem = StyleSheet.create({
  container: {
    marginBottom: 5,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.white,
    borderRadius: 5
  },
  item: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  avatar: {
    borderWidth: 1,
    borderColor: COLORS.borderLight,
    borderRadius: 50,
    height: 52,
    width: 52,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 15,
  },
  number: {
    fontFamily: 'PoppinsBold',
    fontSize: 22,
    color: COLORS.primary
  },
  author: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: COLORS.textLight,
    marginTop: -5,
  },
  title: {
    fontSize: 18,
    fontFamily: 'PoppinsBold',
    lineHeight: 24
  },
  buttonRight: {
    padding: 20
  }
});

function Downloads (props: NavigationProps) {
  const {
    setHymnId,
    authors,
    hymns,
    setHymns,
    hymnId,
    setStartDownload,
    startDownload
  } = useGlobalContext();
  const [selectedId, setSelectedId] = useState<number>();
  const [progressDownload, setProgressDownload] = useState<number>(0);
  const [currentHymn, setCurrentHymn] = useState<number>(0);

  const renderItem = ({item}: {item: HymnProps}) => {
    const selected = item.number === selectedId;

    const selectItem = () => {
      setSelectedId(item.number);
      setHymnId(item.number);
      props.navigation?.navigate(ROUTES.HOME_DRAWER);
    }
    
    const onDelete = async () => {
      if (Platform.OS !== 'web') {
        setStartDownload(true);
        const db = await openDatabase();
        db.transaction((tx) => {
          tx.executeSql(
            'UPDATE hymns SET download = 0 WHERE id = ?;',
            [hymnId]
          );
        });
        setHymns(hymns.map(item => {
          if (item.id === hymnId) {
            item.download = 0;
          }
          return item;
        }));
        const fileUri: string = `${FileSystem.documentDirectory}${getNameAudio(hymnId)}`;
        const fileInfo = await FileSystem.getInfoAsync(fileUri);
        if (fileInfo?.exists) {
          await FileSystem.deleteAsync(fileUri);
        }
        setStartDownload(false);
      }
    }

    return (
      <Item
        item={item}
        onPress={selectItem}
        onDelete={onDelete}
        selected={selected}
        authors={authors}
      />
    );
  };

  const closeSearch = () => {
    props.navigation?.goBack();
  }

  const downloadProgressCallback = (downloadProgress: FileSystem.DownloadProgressData) => {
    const progress = Math.round((downloadProgress.totalBytesWritten / downloadProgress.totalBytesExpectedToWrite) * 100);
    console.log(progress, downloadProgress.totalBytesWritten, downloadProgress.totalBytesExpectedToWrite, (downloadProgress.totalBytesWritten / downloadProgress.totalBytesExpectedToWrite) * 100)
    setProgressDownload(progress);
  };

  const downloadFile = async (number: number) => {
    const fileUri: string = `${FileSystem.documentDirectory}${getNameAudio(number)}`;
    const uri: string = getUriAudio(number);
    setCurrentHymn(number);

    try {
      const fileInfo = await FileSystem.getInfoAsync(fileUri);

      setProgressDownload(0);
      if (!fileInfo?.exists) {
        const downloadResumable = FileSystem.createDownloadResumable(
          uri,
          fileUri,
          {},
          downloadProgressCallback
        );
        await downloadResumable.downloadAsync();
        
        console.log('DOWNLOAD ITEM', fileUri);
      } else {
        setProgressDownload(100);
        console.log('YA EXISTE');
      }
    } catch (error) {
      console.log(`ERROR: ${error}`);
    }
  }

  const TOTAL_HYMNS = 317;

  const allDownload = async () => {
    const downloadPipelined = (items: number[], maxConcurrency = 1) => {
      const queue = [...items];

      const workerFn = async () => {
        while (queue.length > 0) {
          await downloadFile(queue.shift() || 0); 
        }
      }

      const workers = Array(maxConcurrency).fill(workerFn());
      console.log('WORKERS: ', workers);

      return Promise.all(workers);
    }

    setStartDownload(true);
    await downloadPipelined(Array.from({length: TOTAL_HYMNS}, (_, i) => i + 1))
    const db = await openDatabase();
    db.transaction((tx) => {
      tx.executeSql(
        'UPDATE hymns SET download = 1;',
        [hymnId]
      );
    });
    setHymns(hymns.map(item => {
      item.download = 1;
      return item;
    }))
    setStartDownload(false);
  }

  const getWidth = () => {
    return `${currentHymn * 100 / TOTAL_HYMNS}%`;
  }

  const ITEM_HEIGHT = 72;
  const keyExtractor = useCallback((item: HymnProps) => String(item.id), []);
  const getItemLayout = useCallback((_: any, index: number) => ({length: ITEM_HEIGHT, offset: ITEM_HEIGHT * index, index}), []);

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar backgroundColor={COLORS.primary} />
      <View style={stylesHeader.container}>
        <View style={stylesHeader.containerButton}>
          <TouchableOpacity
            style={stylesHeader.buttonLeft}
            onPress={closeSearch}>
            <Icon><BackIcon /></Icon>
          </TouchableOpacity>
        </View>
        <View style={stylesHeader.containerLabel}>
          <Text style={stylesHeader.label}>Mis descargas</Text>
        </View>
      </View>
      <View style={styles.body}>
        {!startDownload && hymns.filter(h => h.download).length < TOTAL_HYMNS && <><TouchableOpacity
            style={styles.buttonDownload}
            onPress={allDownload}>
            <Icon><DownloadIcon /></Icon>
            <Text style={styles.label}>Descargar todo (930 Mb)</Text>
          </TouchableOpacity>
          <Text style={styles.help}>Necesita 930 Mb. de espacio en su teléfono y una conexión wi-fi para descargar todo el himnario juvenil.</Text>
        </>}
        {startDownload && <View style={{ marginBottom: 10 }}>
          <Text>Himno {currentHymn} - {progressDownload}%</Text>
          <View style={[styles.timer, { width: `${progressDownload}%` }]}></View>
          <Text>{currentHymn} de {TOTAL_HYMNS} himnos</Text>
          <View style={[styles.timer, { width: getWidth() }]}></View>
        </View>}
        {hymns.length > 0 && <FlatList
          data={hymns.filter(item => item.download && item.download === 1)}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          extraData={selectedId}
          getItemLayout={getItemLayout}
          initialNumToRender={ 10 }
          windowSize={ 10 } 
          maxToRenderPerBatch={ 10 }
        />}
        {hymns.length === 0 && <Text style={styles.filterEmpty}>
          No tiene ningún favorito seleccionado.
        </Text>}
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flex: 1,
    position: 'relative',
  },
  body: {
    flex: 1,
    backgroundColor: COLORS.background,
    padding: 8 
  },
  filterEmpty: {
    textAlign: 'center',
    fontFamily: 'Poppins',
    marginTop: 10
  },
  label: {
    fontFamily: 'Poppins',
    color: COLORS.white,
    fontSize: 16,
  },
  help: {
    fontFamily: 'Poppins',
    color: COLORS.text,
    fontSize: 14,
    lineHeight: 20,
    margin: 10
  },
  buttonDownload: {
    borderWidth: 1,
    borderColor: COLORS.primary,
    backgroundColor: COLORS.primary,
    borderRadius: 8,
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
    color: 'white',
    paddingVertical: 8
  },
  timer: {
    position: 'relative',
    borderTopColor: COLORS.warning,
    borderTopWidth: 6,
    width: 0,
    marginTop: 2,
    marginBottom: 12
  },
});

const stylesHeader = StyleSheet.create({
  container: {
    backgroundColor: COLORS.primary,
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 50
  },
  containerButton: {
    width: 52
  },
  buttonLeft: {
    padding: 10,
    marginTop: 5,
    marginHorizontal: 5,
    height: 40
  },
  containerLabel: {
    flex: 1,
    justifyContent: 'center',
  },
  label: {
    fontFamily: 'PoppinsBold',
    color: COLORS.white,
    fontSize: 18,
  },
});

export default Downloads;
